import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { track } from "../lib/mixpanel"

// Phase 3.7 Tier 3 — partner usage dashboard.
//
// Mirrors integration_overview_controller.js (60s polling, in-place
// tile + chart updates, no full re-render). Two charts mount on
// connect(): the stacked status-class bar (14d) and the p95 line.
//
// Mixpanel: "Partner Usage Dashboard Viewed" fires once on connect().
// We deliberately do NOT re-fire on poll-driven refreshes.
export default class extends Controller {
  static targets = [
    "lastUpdated",
    "tileTotalCalls",
    "tileSuccessRate",
    "tileP95Latency",
    "tileRateLimit",
    "statusCanvas",
    "latencyCanvas",
    "topVolumeBody",
    "topErrorBody",
    "perKeyBody"
  ]

  static values = {
    refreshUrl: String,
    refreshIntervalMs: { type: Number, default: 60000 },
    statusSeries: String,
    latencySeries: String,
    partnerCompanyId: Number,
    apiKeyCount: Number
  }

  connect() {
    this._statusChart = null
    this._latencyChart = null
    this._initStatusChart()
    this._initLatencyChart()
    this._startPolling()
    track("Partner Usage Dashboard Viewed", {
      partner_company_id: this.hasPartnerCompanyIdValue ? this.partnerCompanyIdValue : null,
      api_key_count: this.hasApiKeyCountValue ? this.apiKeyCountValue : null
    })
  }

  disconnect() {
    this._stopPolling()
    if (this._statusChart) this._statusChart.destroy()
    if (this._latencyChart) this._latencyChart.destroy()
    this._statusChart = null
    this._latencyChart = null
  }

  refreshNow() {
    this._fetchAndRender()
  }

  // ── Polling ───────────────────────────────────────────────────

  _startPolling() {
    this._stopPolling()
    if (!this.refreshUrlValue) return
    this._timer = setInterval(() => this._fetchAndRender(),
                              this.refreshIntervalMsValue)
  }

  _stopPolling() {
    if (this._timer) {
      clearInterval(this._timer)
      this._timer = null
    }
  }

  async _fetchAndRender() {
    if (document.hidden) return
    try {
      const res = await fetch(this.refreshUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return
      const data = await res.json()
      this._applyTiles(data.tiles || {})
      this._applyStatusSeries(data.status_class_series || [])
      this._applyLatencySeries(data.latency_series || [])
      this._applyTopEndpoints(data.top_endpoints || [])
      this._applyTopErrors(data.top_error_endpoints || [])
      this._applyPerKey(data.per_key_breakdown || [])
      this._stampUpdated()
    } catch (e) {
      // network blip; we'll try again next tick
    }
  }

  // ── Tile updates ─────────────────────────────────────────────

  _applyTiles(tiles) {
    if (this.hasTileTotalCallsTarget && tiles.total_calls != null) {
      this.tileTotalCallsTarget.textContent = this._fmtNumber(tiles.total_calls)
    }
    if (this.hasTileSuccessRateTarget) {
      const rate = tiles.success_rate
      this.tileSuccessRateTarget.textContent = (rate == null) ? "—" : `${rate}%`
    }
    if (this.hasTileP95LatencyTarget) {
      const ms = tiles.p95_latency_ms
      this.tileP95LatencyTarget.textContent = (ms == null) ? "—" : `${ms} ms`
    }
    if (this.hasTileRateLimitTarget && tiles.rate_limit) {
      const rl = tiles.rate_limit
      this.tileRateLimitTarget.innerHTML =
        `${rl.remaining} <span class="text-[13px] font-normal text-gray-500">/ ${rl.limit} per minute</span>`
    }
  }

  _applyStatusSeries(series) {
    if (!this._statusChart) return
    this._statusChart.data.labels = series.map((b) => this._labelDay(b.day))
    const datasets = this._statusChart.data.datasets
    datasets[0].data = series.map((b) => b["2xx"] || 0)
    datasets[1].data = series.map((b) => b["3xx"] || 0)
    datasets[2].data = series.map((b) => b["4xx"] || 0)
    datasets[3].data = series.map((b) => b["5xx"] || 0)
    this._statusChart.update("none")
  }

  _applyLatencySeries(series) {
    if (!this._latencyChart) return
    this._latencyChart.data.labels = series.map((b) => this._labelDay(b.day))
    this._latencyChart.data.datasets[0].data = series.map((b) => b.p95_ms)
    this._latencyChart.update("none")
  }

  _applyTopEndpoints(rows) {
    if (!this.hasTopVolumeBodyTarget) return
    this.topVolumeBodyTarget.innerHTML = rows.length === 0
      ? `<tr><td colspan="2" class="px-4 py-6 text-[13px] text-gray-500 text-center">No traffic in the last 24 hours.</td></tr>`
      : rows.map((r) => `
          <tr>
            <td class="px-4 py-1.5 font-mono text-[12px] text-gray-800 truncate">${this._escape(r.endpoint_pattern)}</td>
            <td class="px-4 py-1.5 text-right text-gray-600 whitespace-nowrap">${this._fmtNumber(r.request_count)}</td>
          </tr>`).join("")
  }

  _applyTopErrors(rows) {
    if (!this.hasTopErrorBodyTarget) return
    this.topErrorBodyTarget.innerHTML = rows.length === 0
      ? `<tr><td colspan="4" class="px-4 py-6 text-[13px] text-gray-500 text-center">No errors in the last seven days.</td></tr>`
      : rows.map((r) => `
          <tr>
            <td class="px-4 py-1.5 font-mono text-[12px] text-gray-800 truncate">${this._escape(r.endpoint_pattern)}</td>
            <td class="px-4 py-1.5 text-right text-gray-600 whitespace-nowrap">${this._fmtNumber(r.total)}</td>
            <td class="px-4 py-1.5 text-right text-rose-700 whitespace-nowrap">${this._fmtNumber(r.errors)}</td>
            <td class="px-4 py-1.5 text-right font-medium text-rose-700 whitespace-nowrap">${r.error_rate}%</td>
          </tr>`).join("")
  }

  _applyPerKey(rows) {
    if (!this.hasPerKeyBodyTarget) return
    this.perKeyBodyTarget.innerHTML = rows.map((r) => `
      <tr>
        <td class="px-4 py-1.5">
          <div class="font-medium text-gray-900 truncate">${this._escape(r.name)}</div>
          <div class="font-mono text-[11px] text-gray-500">${this._escape(r.token_prefix)}</div>
        </td>
        <td class="px-4 py-1.5 text-[12px] capitalize text-gray-600">${r.status}</td>
        <td class="px-4 py-1.5 text-right text-gray-700 whitespace-nowrap">${this._fmtNumber(r.total_calls)}</td>
        <td class="px-4 py-1.5 text-right text-gray-700 whitespace-nowrap">${r.success_rate == null ? "—" : `${r.success_rate}%`}</td>
        <td class="px-4 py-1.5 text-right text-gray-700 whitespace-nowrap">${r.p95_latency_ms == null ? "—" : `${r.p95_latency_ms} ms`}</td>
        <td class="px-4 py-1.5 text-right text-emerald-700 whitespace-nowrap">${r.breakdown["2xx"] || 0}</td>
        <td class="px-4 py-1.5 text-right text-sky-700 whitespace-nowrap">${r.breakdown["3xx"] || 0}</td>
        <td class="px-4 py-1.5 text-right text-amber-700 whitespace-nowrap">${r.breakdown["4xx"] || 0}</td>
        <td class="px-4 py-1.5 text-right text-rose-700 whitespace-nowrap">${r.breakdown["5xx"] || 0}</td>
      </tr>`).join("")
  }

  _stampUpdated() {
    if (!this.hasLastUpdatedTarget) return
    const now = new Date()
    const h = String(now.getHours()).padStart(2, "0")
    const m = String(now.getMinutes()).padStart(2, "0")
    this.lastUpdatedTarget.textContent = `Updated ${h}:${m}`
  }

  // ── Chart init ────────────────────────────────────────────────

  _initStatusChart() {
    if (!this.hasStatusCanvasTarget) return
    const series = this._parseSeries(this.statusSeriesValue)
    this._statusChart = new Chart(this.statusCanvasTarget, {
      type: "bar",
      data: {
        labels: series.map((b) => this._labelDay(b.day)),
        datasets: [
          { label: "2xx", data: series.map((b) => b["2xx"] || 0), backgroundColor: "rgba(16,185,129,0.8)", borderRadius: 2, stack: "s" },
          { label: "3xx", data: series.map((b) => b["3xx"] || 0), backgroundColor: "rgba(14,165,233,0.7)", borderRadius: 2, stack: "s" },
          { label: "4xx", data: series.map((b) => b["4xx"] || 0), backgroundColor: "rgba(245,158,11,0.85)", borderRadius: 2, stack: "s" },
          { label: "5xx", data: series.map((b) => b["5xx"] || 0), backgroundColor: "rgba(244,63,94,0.85)", borderRadius: 2, stack: "s" }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { stacked: true, ticks: { font: { size: 9 }, maxRotation: 0, autoSkipPadding: 12 }, grid: { display: false } },
          y: { stacked: true, beginAtZero: true, ticks: { font: { size: 9 }, precision: 0 }, grid: { color: "rgba(0,0,0,0.04)" } }
        }
      }
    })
  }

  _initLatencyChart() {
    if (!this.hasLatencyCanvasTarget) return
    const series = this._parseSeries(this.latencySeriesValue)
    this._latencyChart = new Chart(this.latencyCanvasTarget, {
      type: "line",
      data: {
        labels: series.map((b) => this._labelDay(b.day)),
        datasets: [
          {
            label: "p95",
            data: series.map((b) => b.p95_ms),
            borderColor: "rgba(56,189,248,1)",
            backgroundColor: "rgba(56,189,248,0.18)",
            tension: 0.3,
            spanGaps: true,
            fill: true,
            pointRadius: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { font: { size: 9 }, maxRotation: 0, autoSkipPadding: 12 }, grid: { display: false } },
          y: { beginAtZero: true, ticks: { font: { size: 9 }, callback: (v) => `${v}` }, grid: { color: "rgba(0,0,0,0.04)" } }
        }
      }
    })
  }

  // ── Helpers ───────────────────────────────────────────────────

  _parseSeries(raw) {
    if (!raw) return []
    try { return JSON.parse(raw) } catch (_e) { return [] }
  }

  _labelDay(iso) {
    if (!iso) return ""
    const m = iso.match(/(\d{4})-(\d{2})-(\d{2})/)
    if (!m) return iso
    return `${m[2]}/${m[3]}`
  }

  _fmtNumber(n) {
    if (n == null) return "0"
    return Number(n).toLocaleString()
  }

  _escape(text) {
    const div = document.createElement("div")
    div.textContent = text == null ? "" : String(text)
    return div.innerHTML
  }
}
